import { useMemo } from 'react';
import { FullProduct } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { OperationDetails } from 'screens/Applications&WorkOrders/Components/types';
import useWorkOrderDetails from 'screens/Applications&WorkOrders/WorkOrders/Hooks/useWorkOrderDetails';
import useApplicationDetails from 'screens/Applications&WorkOrders/Applications/Hooks/useApplicationDetails';
import useOperationProducts from 'screens/Applications&WorkOrders/Components/hooks/useOperationProducts';
import useOperationProperties, {
  OperationPropertyVO
} from 'screens/Applications&WorkOrders/Components/hooks/useOperationProperties';
import { RecordSourceType } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { LinkedRecordDetailVM, LinkedRecordRow } from '../../../types';

const EMPTY_PRODUCTS: FullProduct[] = [];
const EMPTY_PROPERTIES: OperationPropertyVO[] = [];

const EMPTY_VM: LinkedRecordDetailVM = {
  details: undefined,
  products: EMPTY_PRODUCTS,
  propertiesData: EMPTY_PROPERTIES,
  isLoading: false,
  hasError: false
};

/**
 * Resolves the selected linked record's detail data and adapts it into the
 * shape `DetailsTabs` consumes.
 *
 * Every underlying hook is called unconditionally (rules of hooks); each gets a
 * real id only when the selected record matches its type, and null otherwise,
 * which those hooks treat as "do nothing".
 */
const useLinkedRecordDetails = (
  record: LinkedRecordRow | null
): LinkedRecordDetailVM => {
  const isWorkOrder = record?.type === RecordSourceType.WORK_ORDER;
  const isApplication = record?.type === RecordSourceType.APPLICATION;

  const workOrderId = isWorkOrder ? record?.id ?? null : null;
  const applicationId = isApplication ? record?.id ?? null : null;

  const {
    workOrderDetails,
    isLoading: isLoadingWorkOrder,
    errors: workOrderErrors
  } = useWorkOrderDetails(workOrderId);
  const {
    products: workOrderProducts,
    isLoading: isLoadingWorkOrderProducts
  } = useOperationProducts(workOrderId, 'work-order');
  const {
    propertiesData: workOrderProperties,
    isLoading: isLoadingWorkOrderProperties
  } = useOperationProperties(workOrderId, 'work-order');

  const {
    applicationDetails,
    isLoading: isLoadingApplication,
    errors: applicationErrors
  } = useApplicationDetails(applicationId);
  const {
    products: applicationProducts,
    isLoading: isLoadingApplicationProducts
  } = useOperationProducts(applicationId, 'application');
  const {
    propertiesData: applicationProperties,
    isLoading: isLoadingApplicationProperties
  } = useOperationProperties(applicationId, 'application');

  return useMemo<LinkedRecordDetailVM>(() => {
    if (isWorkOrder) {
      const hasError = Boolean(workOrderErrors);
      return {
        details: workOrderDetails as OperationDetails | undefined,
        products: workOrderProducts ?? EMPTY_PRODUCTS,
        propertiesData: workOrderProperties ?? EMPTY_PROPERTIES,
        tankInformation: workOrderDetails?.tankInformation,
        // These hooks seed `data` as `{}` with `isLoading === false`, so treat
        // "no real details yet" as still loading — otherwise the tabs flash
        // empty before the first response lands.
        isLoading:
          !hasError &&
          (isLoadingWorkOrder ||
            isLoadingWorkOrderProducts ||
            isLoadingWorkOrderProperties ||
            !workOrderDetails?.id),
        hasError
      };
    }

    if (isApplication) {
      const hasError = Boolean(applicationErrors);
      return {
        details: applicationDetails as OperationDetails | undefined,
        products: applicationProducts ?? EMPTY_PRODUCTS,
        propertiesData: applicationProperties ?? EMPTY_PROPERTIES,
        tankInformation: applicationDetails?.tankInformation,
        isLoading:
          !hasError &&
          (isLoadingApplication ||
            isLoadingApplicationProducts ||
            isLoadingApplicationProperties ||
            !applicationDetails?.id),
        hasError
      };
    }

    return EMPTY_VM;
  }, [
    isWorkOrder,
    isApplication,
    workOrderDetails,
    workOrderProducts,
    workOrderProperties,
    workOrderErrors,
    isLoadingWorkOrder,
    isLoadingWorkOrderProducts,
    isLoadingWorkOrderProperties,
    applicationDetails,
    applicationProducts,
    applicationProperties,
    applicationErrors,
    isLoadingApplication,
    isLoadingApplicationProducts,
    isLoadingApplicationProperties
  ]);
};

export default useLinkedRecordDetails;
